import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useId } from '@reach/auto-id';
import { getVariantPath } from '@uswitch/spark.utils';
import { BsToggleOff, BsToggleOn } from '@uswitch/spark-icons/bs';
import { MdCheckBox, MdCheckBoxOutlineBlank, MdRadioButtonChecked, MdRadioButtonUnchecked } from '@uswitch/spark-icons/md';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { jsx as _jsx } from "theme-ui/jsx-runtime";
import { jsxs as _jsxs } from "theme-ui/jsx-runtime";
var labelVariantPath = getVariantPath({
  id: 'booleanInput',
  element: 'label'
});
export var BooleanInput = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var disabled = _ref.disabled,
      className = _ref.className,
      idProp = _ref.id,
      Icon = _ref.icon,
      type = _ref.type,
      image = _ref.image,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, ["disabled", "className", "id", "icon", "type", "image", "children"]);

  var id = useId(idProp);
  var labelSx = {
    position: 'relative',
    variant: labelVariantPath(disabled ? 'disabled' : 'base'),
    userSelect: 'none'
  };

  if (!disabled) {
    labelSx = _objectSpread(_objectSpread({}, labelSx), {}, {
      ':hover, ._force-hover &': {
        variant: labelVariantPath('base', 'hover')
      },
      ':focus, ._force-focus &': {
        variant: labelVariantPath('base', 'focus')
      }
    });
  }

  if (image) {
    labelSx = _objectSpread(_objectSpread({}, labelSx), {}, {
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      fontWeight: 600,
      padding: 18,
      display: 'flex',
      fontSize: 'base',
      lineHeight: 'base',
      svg: {
        alignSelf: 'flex-start',
        mb: 18
      },
      img: {
        mb: 16,
        objectFit: 'cover'
      }
    });
  }

  return _jsxs(Fragment, {
    children: [_jsx("input", _objectSpread({
      ref: ref,
      tabIndex: -1,
      disabled: disabled,
      id: id,
      type: type,
      sx: {
        position: 'absolute',
        zIndex: -1,
        appearance: 'none',
        opacity: 0,
        '&:checked + label': {
          variant: labelVariantPath('checked'),
          '.icon-checked': {
            display: 'block'
          },
          '.icon-not-checked': {
            display: 'none'
          }
        },
        '&:not(:checked) + label': {
          variant: labelVariantPath('unChecked'),
          '.icon-checked': {
            display: 'none'
          },
          '.icon-not-checked': {
            display: 'block'
          }
        }
      }
    }, props)), _jsxs("label", {
      tabIndex: 0,
      role: "presentation",
      htmlFor: id,
      className: className,
      sx: labelSx,
      children: [_jsx(Icon, {
        sx: {
          mr: 'xxs',
          alignSelf: image ? 'flex-start' : undefined
        }
      }), image && _jsx("div", {
        sx: {
          maxWidth: '109px',
          img: {
            maxWidth: '100%'
          }
        },
        children: image
      }), children]
    })]
  });
});
export var RadioIcon = function RadioIcon(props) {
  return _jsxs("div", _objectSpread(_objectSpread({}, props), {}, {
    sx: {
      'img,svg': {
        size: '22px'
      }
    },
    children: [_jsx(MdRadioButtonChecked, {
      className: "icon-checked"
    }), _jsx(MdRadioButtonUnchecked, {
      className: "icon-not-checked"
    })]
  }));
};
export var CheckboxIcon = function CheckboxIcon(props) {
  return _jsxs("div", _objectSpread(_objectSpread({}, props), {}, {
    sx: {
      'img,svg': {
        size: '22px'
      }
    },
    children: [_jsx(MdCheckBoxOutlineBlank, {
      className: "icon-not-checked"
    }), _jsx(MdCheckBox, {
      className: "icon-checked"
    })]
  }));
};
export var ToggleIcon = function ToggleIcon(props) {
  return _jsxs("div", _objectSpread(_objectSpread({
    sx: {
      'img,svg': {
        width: 'auto',
        height: [28, 40]
      }
    }
  }, props), {}, {
    children: [_jsx(BsToggleOff, {
      className: "icon-not-checked"
    }), _jsx(BsToggleOn, {
      className: "icon-checked"
    })]
  }));
};
var sharedPropTypes = {
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  icon: PropTypes.elementType,
  image: PropTypes.element
};
BooleanInput.propTypes = _objectSpread(_objectSpread({}, sharedPropTypes), {}, {
  type: PropTypes.oneOf(['radio', 'checkbox'])
});
BooleanInput.defaultProps = {
  disabled: false,
  type: 'checkbox',
  icon: CheckboxIcon
};
BooleanInput.displayName = 'BooleanInput';
export var Checkbox = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var props = _extends({}, _ref2);

  return _jsx(BooleanInput, _objectSpread(_objectSpread({
    type: 'checkbox'
  }, props), {}, {
    ref: ref
  }));
});
Checkbox.propTypes = _objectSpread({}, sharedPropTypes);
Checkbox.defaultProps = {
  disabled: false,
  icon: CheckboxIcon
};
Checkbox.displayName = 'Checkbox';
export var Toggle = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return _jsx(BooleanInput, _objectSpread(_objectSpread({
    type: "checkbox"
  }, props), {}, {
    ref: ref
  }));
});
Toggle.propTypes = _objectSpread({}, sharedPropTypes);
Toggle.defaultProps = {
  disabled: false,
  icon: ToggleIcon
};
Toggle.displayName = 'Toggle';
export var Radio = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return _jsx(BooleanInput, _objectSpread(_objectSpread({
    type: "radio"
  }, props), {}, {
    ref: ref
  }));
});
Radio.propTypes = _objectSpread({}, sharedPropTypes);
Radio.defaultProps = {
  disabled: false,
  icon: RadioIcon
};
Radio.displayName = 'Radio';